import { GlobalToken, theme } from "antd";
import { createStyles } from "antd-style";
import type { ViewConfigurationComponent } from "@rumbledirect/types";

const styleUtils = createStyles(
  ({ css }, { themeToken }: { themeToken: GlobalToken }) => ({
    itemWrapper: css`
      display: flex;
      flex-direction: column;
      border-radius: 16px;
      color: white;
      overflow: hidden;
      height: 100%;
      cursor: pointer;
      color: #fff !important;
      text-decoration: none !important;
      border: none;
      padding: 0;
      background: none;
      font: inherit;
      text-align: inherit;
    `,
    gridLayout: css`
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 30px;

      @media print, screen and (max-width: 56.25em) {
        grid-template-columns: repeat(1, minmax(0, 1fr));
      }
    `,
    stackLayout: css`
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-top: 4rem;
    `,
    // Full-width tiles below a grid: the image takes one grid column, the content the rest.
    bannerLayout: css`
      display: flex;
      flex-direction: column;
      gap: 30px;
      margin-top: 30px;

      & > * {
        flex-direction: row;
      }

      & > * > :first-child:not(:only-child) {
        flex-shrink: 0;
        width: calc((100% - 60px) / 3);
        height: auto;
        padding-bottom: 0;
      }

      @media screen and (min-width: 56.26em) {
        & > * > :last-child {
          align-items: flex-start;
          padding: 1.5rem 2rem;
        }

        & > * > :last-child > * {
          text-align: left;
        }
      }

      @media print, screen and (max-width: 56.25em) {
        & > * {
          flex-direction: column;
        }

        & > * > :first-child:not(:only-child) {
          width: 100%;
          height: 1px;
          padding-bottom: 66%;
        }
      }
    `,
    itemImageWrapper: css`
      height: 1px;
      padding-bottom: 66%;
      position: relative;
      width: 100%;
    `,
    itemImage: css`
      position: absolute;
      height: 100%;
      width: 100%;
      top: 0;
      left: 0;
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center;
    `,
    itemContent: css`
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 1.5rem .6rem;
      background-color: ${themeToken.colorPrimary} !important;
      flex-grow: 1;
      font-size: 16px;
    `,
    itemContentTitle: css`
      margin: 0 0 .5rem;
      font-weight: 700;
      line-height: 1.2;
      font-size: 1.3rem;
      text-align: center;
    `,
    itemContentText: css`
      text-align: center;
      margin-top: 0;
      margin-bottom: 24px !important;
    `,
    itemButtonFooter: css`
      margin-top: auto;
      font-weight: 700;
      text-align: center;
    `,
    itemButtonFooterArrow: css`
      border: solid #fff;
      border-width: 0 4px 4px 0;
      height: 13px;
      width: 13px;
      display: inline-flex;
      margin-left: 3px;
      position: relative;
      top: 1px;
      transform: rotate(-45deg);
    `,
    primaryLinkColor: css`
      color: ${themeToken.colorPrimary || "#ffffff"} !important;
    `,
    primaryLink: css`
      color: ${themeToken.colorPrimary || "#ffffff"} !important;
      margin-top: 1.5rem;
      display: flex;
    `,
    footerTitle: css`
      margin-top: 2.25rem;
      line-height: 1.2;
    `,
  }),
);

interface ViewConfigurationRendererProps {
  components: ViewConfigurationComponent[];
}

const ActionCardTile = ({
  component,
  styles,
}: {
  component: ViewConfigurationComponent;
  styles: ReturnType<typeof styleUtils>["styles"];
}) => {
  const content = (
    <>
      {component.image && (
        <div className={styles.itemImageWrapper}>
          <div
            className={styles.itemImage}
            style={{ backgroundImage: `url("${component.image}")` }}
          />
        </div>
      )}
      <div className={styles.itemContent}>
        <h3 className={styles.itemContentTitle}>{component.title}</h3>
        <p className={styles.itemContentText}>{component.description}</p>
        <span className={styles.itemButtonFooter}>
          <span>{component.action?.text}</span>
          <span className={styles.itemButtonFooterArrow} />
        </span>
      </div>
    </>
  );

  if (component.action?.url) {
    return (
      <a
        className={styles.itemWrapper}
        target="_blank"
        rel="noreferrer"
        href={component.action.url}
      >
        {content}
      </a>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={styles.itemWrapper}
      data-form-id={component.action?.formId}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.currentTarget.click();
        }
      }}
    >
      {content}
    </div>
  );
};

const ActionCardLink = ({
  component,
  styles,
}: {
  component: ViewConfigurationComponent;
  styles: ReturnType<typeof styleUtils>["styles"];
}) => (
  <div>
    <h2 className={styles.footerTitle}>{component.title}</h2>
    <p>{component.description}</p>
    <span className={styles.primaryLink}>
      ►&nbsp;
      <a
        target="_blank"
        rel="noreferrer"
        href={component.action?.url}
        className={styles.primaryLinkColor}
      >
        {component.action?.text}
      </a>
    </span>
  </div>
);

const renderComponent = (
  component: ViewConfigurationComponent,
  styles: ReturnType<typeof styleUtils>["styles"],
) => {
  if (component.type === "actionCard" && component.image) {
    return (
      <ActionCardTile
        key={component.key}
        component={component}
        styles={styles}
      />
    );
  }
  if (component.type === "actionCard") {
    return (
      <ActionCardLink
        key={component.key}
        component={component}
        styles={styles}
      />
    );
  }
  return null;
};

const Section = ({
  section,
  styles,
}: {
  section: ViewConfigurationComponent;
  styles: ReturnType<typeof styleUtils>["styles"];
}) => {
  const layoutClass = {
    grid: styles.gridLayout,
    stack: styles.stackLayout,
    banner: styles.bannerLayout,
  }[section.layout ?? "stack"];

  return (
    <div className={layoutClass}>
      {section.components?.map((child) => renderComponent(child, styles))}
    </div>
  );
};

export const ViewConfigurationRenderer = ({
  components,
}: ViewConfigurationRendererProps) => {
  const { token } = theme.useToken();
  const { styles } = styleUtils({ themeToken: token });

  return (
    <div>
      {components.map((component) => {
        if (component.type === "section") {
          return (
            <Section key={component.key} section={component} styles={styles} />
          );
        }
        return renderComponent(component, styles);
      })}
    </div>
  );
};
